import { useCallback, useEffect, useMemo, useState } from "react";
import { forEach } from "lodash";
import { countType, csvPriceRowType, priceRowType, summaryRowType, lookupService, termLookup, YearDaysForCalculation } from "./appConstants";
import { currencyFormatter, getDateForPriceRow, sumCount } from "./methods";
import AuthService from "../services/auth.service";

export function useIsMounted() {
  const [isMounted, setIsMounted] = useState(true);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  return (isMounted);
}

export function useSet(originalSet: any, open: boolean) {
  const [set, setSet] = useState<any>(new Set([originalSet]));
  const [original, setOriginal] = useState<any>(new Set([originalSet]));

  // every time open is toggled we update the set
  useEffect(() => {
    setSet(new Set([...originalSet]));
    setOriginal(new Set([...originalSet]));
  }, [open, originalSet]);

  // if the set has item, remove it. otherwise, add it
  const toggle = (item: any) => {
    if (set.has(item)) {
      set.delete(item);
    } else {
      set.add(item);
    }
    setSet(new Set([...set]));
  };

  const union = (array: any) => {
    setSet(new Set([...set, ...array]));
  };

  const difference = (array: any) => {
    const difference = new Set([...set].filter((x) => !array.includes(x)));
    setSet(difference);
  };

  // returns true if all elements in subset are in NetworkElements
  function isSubset(subset: number[]) {
    let isSubset = true;
    subset.forEach((element: number) => {
      if (!set.has(element)) {
        isSubset = false;
      }
    });
    return isSubset;
  }

  const areEqual = useMemo(() => {
    if (JSON.stringify([...set]) === JSON.stringify([...original])) {
      return true;
    }
    return false;
  }, [set, original]);

  const array = useMemo(() => {
    return Array.from(set);
  }, [set]);

  return {
    array,
    union,
    difference,
    toggle,
    isSubset,
    areEqual,
  };
}

export function useNetworkElement(initialNetworkElements: any, open: boolean) {
  const [networkElements, setNetworkElements] = useState<ne[]>(initialNetworkElements);

  type ne = { on_net: number; nrCount: countType; bdCount: countType; id: number; city: string; state: string; ne_name: string };
  const sortNE = useCallback((list: ne[]) => {
    list.sort((a: ne, b: ne) => b.on_net - a.on_net);
    list.sort((a: ne, b: ne) => sumCount(b.nrCount) + sumCount(b.bdCount) - sumCount(a.nrCount) - sumCount(a.bdCount));
    return list;
  }, []);

  useEffect(() => {
    setNetworkElements(sortNE(initialNetworkElements));
  }, [open, initialNetworkElements, sortNE]);

  const NEArray = useMemo(() => {
    return networkElements.map((ne: any) => {
      return ne.id;
    });
  }, [networkElements]);

  function likeNE(ne: any, regex: RegExp) {
    const array = [ne.city, ne.state, ne.ne_name].join(", ").match(regex);
    if (array && array.length > 0) {
      return true;
    }
    return false;
  }

  const filterNetworkElements = useCallback(
    ({ filterByBD = false, filterByNR = false, filterByOnNet = false, filterByPrivate = false }, searchText: string) => {
      let tempNE = [...initialNetworkElements];
      if (filterByNR && filterByBD) {
        tempNE = tempNE.filter((ne: { nrCount: countType; bdCount: countType }) => sumCount(ne.nrCount) + sumCount(ne.bdCount) > 0);
      } else if (filterByNR) {
        tempNE = tempNE.filter((ne: { nrCount: countType }) => sumCount(ne.nrCount) > 0);
      } else if (filterByBD) {
        tempNE = tempNE.filter((ne: { bdCount: countType }) => sumCount(ne.bdCount) > 0);
      }
      if (filterByOnNet) {
        tempNE = tempNE.filter((ne: { on_net: boolean }) => ne.on_net);
      }
      if (filterByPrivate) {
        tempNE = tempNE.filter((ne: { public: boolean }) => !ne.public);
      }
      if (searchText) {
        const regex = new RegExp(searchText, "i");
        tempNE = tempNE.filter((ne: any) => likeNE(ne, regex));
      }
      setNetworkElements(sortNE(tempNE));
    },
    [initialNetworkElements, sortNE, setNetworkElements]
  );

  return {
    networkElements,
    setNetworkElements,
    NEArray,
    filterNetworkElements,
  };
}

export function useSummaryPriceRow() {
  const [incomeRows, setIncomeRows] = useState<summaryRowType[]>([]);
  const [expenseRows, setExpenseRows] = useState<summaryRowType[]>([]);
  const [sums, setSums] = useState<{ mrcIncome: number; mrcExpense: number; totalIncome: number; totalExpense: number }>({
    totalIncome: 0,
    totalExpense: 0,
    mrcIncome: 0,
    mrcExpense: 0,
  });

  const handleSetIncomeExpenseRows = useCallback(
    (
      rows: any,
      sums: {
        incomeMRC: string;
        expenseMRC: string;
        incomeSum: string;
        expenseSum: string;
      }
    ) => {
      const incomeRowTemp: summaryRowType[] = [];
      const expenseRowTemp: summaryRowType[] = [];
      forEach(rows, (value: any) => {
        // loop through each company
        const company = value[0].targetOrg;
        let incomeAvgMRCSum = 0;
        let expenseAvgMRCSum = 0;
        let incomeTotalSum = 0;
        let expenseTotalSum = 0;
        value.forEach((row: priceRowType) => {
          if (row.type === "expense") {
            expenseAvgMRCSum += Number(row.mrcAvg);
            expenseTotalSum += Number(row.mrc) + Number(row.fee);
          } else if (row.type === "income") {
            incomeAvgMRCSum += Number(row.mrcAvg);
            incomeTotalSum += Number(row.mrc) + Number(row.fee);
          }
        });
        if (incomeAvgMRCSum + incomeTotalSum >= 0.01) {
          incomeRowTemp.push({
            company,
            monthlyRecurring: incomeAvgMRCSum,
            currentBillingPeriod: incomeTotalSum,
          });
        }
        if (expenseAvgMRCSum + expenseTotalSum >= 0.01) {
          expenseRowTemp.push({
            company,
            monthlyRecurring: expenseAvgMRCSum,
            currentBillingPeriod: expenseTotalSum,
          });
        }
      });
      if (incomeRowTemp.length > 1)
        incomeRowTemp.push({
          company: "Total",
          currentBillingPeriod: Number(sums.incomeSum),
          monthlyRecurring: Number(sums.incomeMRC),
          total: true,
        });
      if (expenseRowTemp.length > 1)
        expenseRowTemp.push({
          company: "Total",
          currentBillingPeriod: Number(sums.expenseSum),
          monthlyRecurring: Number(sums.expenseMRC),
          total: true,
        });
      setIncomeRows(incomeRowTemp);
      setExpenseRows(expenseRowTemp);
      setSums({
        mrcIncome: Number(sums.incomeMRC),
        mrcExpense: Number(sums.expenseMRC),
        totalExpense: Number(sums.expenseSum),
        totalIncome: Number(sums.incomeSum),
      });
    },
    [setIncomeRows, setExpenseRows, setSums]
  );

  return {
    handleSetIncomeExpenseRows,
    incomeRows,
    expenseRows,
    sums,
  };
}

export function useDropDownSet() {
  const [set, setSet] = useState<any>(new Set());
  const addSet = useCallback(
    (orgs: string[]) => {
      orgs.forEach((org: string) => {
        set.add(org);
      });
      setSet(new Set([...set]));
    },
    [setSet, set]
  );
  const array = useMemo(() => {
    return [...set];
  }, [set]);
  return { array, addSet };
}

export function useCSVData() {
  const [csvData, setData] = useState<{ income: csvPriceRowType[]; expense: csvPriceRowType[] }>();

  const setCSVData = useCallback((incomeRows: priceRowType[], expenseRows: priceRowType[], filters: { start: Date; end: Date }) => {
    const csvIncome: csvPriceRowType[] = [];
    const csvExpense: csvPriceRowType[] = [];
    const getPriceCell = (row: priceRowType["row"]): string => {
      if (!row) return "";
      if (row.term.type === "demand") {
        return `$${currencyFormatter(row.term.charge / 100, 4)}`;
      }
      return `$${currencyFormatter(row.term.charge)}`;
    };
    const getPricePerMonthCell = (row: priceRowType["row"]): string => {
      if (!row) return "";
      let dollars = row?.term.charge || 0;
      if (row.term.type === "demand") {
        dollars /= 100;
        dollars *= (60 * 24 * YearDaysForCalculation) / 12;
        return `$${currencyFormatter(dollars, 4)}`;
      }
      return `$${currencyFormatter(dollars)}`;
    };
    const mapPriceRowToCSV = (row: priceRowType): csvPriceRowType => {
      return {
        id: Math.round(row.row?.id || 0),
        resourceId: row.row?.resourceId || null,
        description: `${row.row?.description.title || ""} ${row.row?.description.subtitle || ""}`,
        location: row.row?.location.title || "",
        provider: row.row?.targetOrg.title || "",
        service: lookupService(row.row?.targetOrg.subtitle || "") || "",
        price: getPriceCell(row.row) || "",
        term: termLookup[row.row?.term.type || ""],
        quantity: parseFloat(row.row?.term.quantity.toFixed(8) || "0"),
        startDate: row.row?.dates.start ? getDateForPriceRow(new Date(row.row?.dates.start)) : "",
        endDate: row.row?.dates.end ? getDateForPriceRow(new Date(row.row?.dates.end)) : "",
        perMonth: getPricePerMonthCell(row.row),
        filterStartDate: getDateForPriceRow(new Date(filters.start)),
        filterEndDate: getDateForPriceRow(new Date(filters.end)),
        total: `$${currencyFormatter(row.fee + row.mrc)}`,
      };
    };

    incomeRows &&
      incomeRows.forEach((row: priceRowType) => {
        csvIncome.push(mapPriceRowToCSV(row));
      });
    expenseRows &&
      expenseRows.forEach((row: priceRowType) => {
        csvExpense.push(mapPriceRowToCSV(row));
      });

    setData({ income: csvIncome, expense: csvExpense });
  }, []);
  return { csvData, setCSVData };
}
